import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { usePermittedQuery } from '@/hooks/usePermittedQuery'
import {
  listBatches,
  getBatch,
  getReadiness,
  getPayrollDashboard,
  getFinalizePreview,
  createBatch,
  finalizeMonth,
  bulkPayrollMonthAction,
  consolidateBatch,
  approveBatch,
  markPaid,
  rejectBatch,
  resubmitBatch,
  voidBatch,
  reverseBatchPayment,
  getBatchSummary,
  listConsolidatedItems,
  getConsolidatedItem,
} from '@/api/services/payroll-batches.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  BulkPayrollMonthAction,
  CreateOrgPayrollBatchDto,
} from '@/features/payroll/types/payroll.types'

export const batchKeys = {
  all: () => ['payroll-batches'] as const,
  list: (page?: number, limit?: number) =>
    ['payroll-batches', 'list', page, limit] as const,
  detail: (batchId: string | null) =>
    ['payroll-batches', 'detail', batchId] as const,
  summary: (batchId: string | null) =>
    ['payroll-batches', 'summary', batchId] as const,
  items: (batchId: string | null, page?: number) =>
    ['payroll-batches', 'items', batchId, page] as const,
  consolidatedItem: (batchId: string | null, employeeId: string | null) =>
    ['payroll-batches', 'consolidated', batchId, employeeId] as const,
  readiness: (year: number, month: number) =>
    ['payroll-batches', 'readiness', year, month] as const,
  dashboard: (year: number, month: number) =>
    ['payroll-batches', 'dashboard', year, month] as const,
  finalizePreview: (year: number, month: number) =>
    ['payroll-batches', 'finalize-preview', year, month] as const,
}

// ─── Queries ───────────────────────────────────────

export function useBatches(page = 1, limit = 20) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.list(page, limit),
    queryFn: () => listBatches({ page, limit }),
    staleTime: 60_000,
  })
}

export function useBatch(batchId: string | null) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.detail(batchId),
    queryFn: () => getBatch(batchId!),
    staleTime: 60_000,
    enabled: !!batchId,
  })
}

export function useBatchReadiness(year: number, month: number, options?: { enabled?: boolean }) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.readiness(year, month),
    queryFn: () => getReadiness(year, month),
    staleTime: 30_000,
    enabled: options?.enabled !== false,
  })
}

export function usePayrollDashboard(year: number, month: number, options?: { enabled?: boolean }) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.dashboard(year, month),
    queryFn: () => getPayrollDashboard(year, month),
    staleTime: 30_000,
    enabled: options?.enabled !== false,
  })
}

export function useFinalizePreview(year: number, month: number, options?: { enabled?: boolean }) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.finalizePreview(year, month),
    queryFn: () => getFinalizePreview(year, month),
    staleTime: 30_000,
    enabled: options?.enabled !== false,
  })
}

export function useBatchSummary(batchId: string | null) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.summary(batchId),
    queryFn: () => getBatchSummary(batchId!),
    staleTime: 60_000,
    enabled: !!batchId,
  })
}

export function useBatchItems(batchId: string | null, page = 1, limit = 20) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.items(batchId, page),
    queryFn: () => listConsolidatedItems(batchId!, { page, limit }),
    staleTime: 60_000,
    enabled: !!batchId,
  })
}

export function useConsolidatedItem(batchId: string | null, employeeId: string | null) {
  return usePermittedQuery({
    module: 'payroll',
    action: 'read_org',
    queryKey: batchKeys.consolidatedItem(batchId, employeeId),
    queryFn: () => getConsolidatedItem(batchId!, employeeId!),
    staleTime: 60_000,
    enabled: !!batchId && !!employeeId,
  })
}

// ─── Mutations ─────────────────────────────────────

export function useCreateBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateOrgPayrollBatchDto) => createBatch(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Batch lifecycle changes lock/revert/unlock company runs — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Payroll batch created.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useFinalizeMonth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateOrgPayrollBatchDto) => finalizeMonth(body),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      if (result.steps.includes('paid')) {
        toast.success('Payroll finalized and marked paid. Employees notified.')
      } else if (result.steps[0] === 'already_paid') {
        toast.info('This month is already paid.')
      } else {
        toast.success(`Finalize progressed: ${result.steps.join(' → ')}`)
      }
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useBulkPayrollMonthAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      action,
      year,
      month,
    }: {
      action: BulkPayrollMonthAction
      year: number
      month: number
    }) => bulkPayrollMonthAction(action, { year, month }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      const summary = `${result.succeeded} succeeded`
      if (result.failed > 0) {
        toast.warning(`${summary}, ${result.failed} failed.`)
      } else if (result.skipped > 0) {
        toast.info(`${summary}, ${result.skipped} skipped.`)
      } else {
        toast.success(summary)
      }
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useConsolidateBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (batchId: string) => consolidateBatch(batchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Batch lifecycle changes lock/revert/unlock company runs — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Batch consolidated.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useApproveBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (batchId: string) => approveBatch(batchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Batch lifecycle changes lock/revert/unlock company runs — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Batch approved.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useMarkPaid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (batchId: string) => markPaid(batchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Batch lifecycle changes lock/revert/unlock company runs — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Batch marked as paid. Employees notified.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useRejectBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: string; reason: string }) =>
      rejectBatch(batchId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Batch lifecycle changes lock/revert/unlock company runs — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Batch rejected. Company runs reverted to calculated.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useResubmitBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (batchId: string) => resubmitBatch(batchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Batch lifecycle changes lock/revert/unlock company runs — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Batch resubmitted.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useVoidBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (batchId: string) => voidBatch(batchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Batch lifecycle changes lock/revert/unlock company runs — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Batch voided. Source records unlocked.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useReverseBatchPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: string; reason: string }) =>
      reverseBatchPayment(batchId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: batchKeys.all() })
      // Reversal returns company runs to approved — refresh run views too.
      qc.invalidateQueries({ queryKey: ['payroll-runs'] })
      toast.success('Payment reversed. The period is open again.')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}
